import React, { useEffect, useLayoutEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import { useQuery } from '../../../../utils/hooks/useQuery';
import Button from '../../../_common/Form/Buttons/Button';
import classes from './repScorecards.module.scss';
import utils from '../../../../utils/utils';
import { getDashboardRepScorecard, getDashboardUserWorkflowRequest } from '../../../../store/actions/dashboard';
import Loader from '../../../_common/Loader/Loader';
import TableAndPagination from '../../../_common/TableAndPagination/TableAndPagination';
import Api from '../../../../api/api';
import accountHelpers from '../../../../utils/accountHelpers';

const tableHeader = [
  {
    name: 'Name',
    path: 'name',
  },
  {
    name: 'Specialty Prescription Count',
    path: 'specialty',
  },
  {
    name: 'Retail Prescription Count',
    path: 'retail',
  },
  {
    name: 'Total Prescription count',
    path: 'sales',
  },
  {
    name: 'Total Sales',
    path: 'revenue',
    type: 'currency',
  },
  {
    name: 'Profit',
    path: 'profit',
    type: 'currency',
  },
  {
    name: 'Margin',
    path: 'margin',
  },
  {
    name: 'Expenses',
    path: 'expenses',
  },
  {
    name: 'New physicians',
    path: 'newPhysicians',
  },
  {
    name: 'New patients',
    path: 'newPatients',
  },
  {
    name: 'Appointments',
    path: 'appointment',
  },
  {
    name: 'Specialty Bonus',
    path: 'bonusSpecialty',
    type: 'currency',
  },
  {
    name: 'Retail Bonus',
    path: 'bonusRetail',
    type: 'currency',
  },
  {
    name: 'Incentive Bonus',
    path: 'incentivesBonus',
    type: 'currency',
  },
  {
    name: 'Total Bonus',
    path: 'totalBonus',
    type: 'currency',
  },
];

const tableHeaderWorkflow = [
  {
    name: 'User',
    path: 'name',
  },
  {
    name: 'Received',
    path: 'r',
  },
  {
    name: 'Received to Process',
    path: 'rp',
  },
  {
    name: 'Received to Schedule',
    path: 'rs',
  },
  {
    name: 'Process to Prior Auth',
    path: 'pp',
  },
  {
    name: 'Process to Copay Assistance',
    path: 'pc',
  },
  {
    name: 'Process to Schedule',
    path: 'ps',
  },
  {
    name: 'Schedule to QA',
    path: 'sq',
  },
  {
    name: 'QA to Fill',
    path: 'qf',
  },
  {
    name: 'Fill to Shipped',
    path: 'fs',
  },
];

const limitCounts = [
  {
    id: 0,
    count: 50,
  },
  {
    id: 1,
    count: 75,
  },
  {
    id: 2,
    count: 100,
  },
  {
    id: 3,
    count: 125,
  },
];

const collectRequestData = (requestData) => {
  const data = utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const RepScorecards = ({ workflow }) => {
  const { query, setQuery, queryToStringify } = useQuery();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dashboardPageData, dashboardTotalPage, dashboardWorkflowData } = useSelector((store) => store.dashboard);

  const [requestData, setRequestData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    page: query.page || 1,
    limit: query.limit || 50,
  });
  const { start, end } = requestData;
  const [loading, setLoading] = useState(false);

  useLayoutEffect(() => {
    (async () => {
      setLoading(true);
      if (workflow) {
        await dispatch(getDashboardUserWorkflowRequest(collectRequestData(requestData)));
      } else {
        await dispatch(getDashboardRepScorecard(collectRequestData(requestData)));
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const newQuery = utils.deleteEmptyKeys({ ...requestData });
    setQuery(newQuery);
  }, [requestData]);

  const onChangeDate = async (currentDate) => {
    setRequestData((prev) => ({ ...prev, start: currentDate[0], end: currentDate[1] }));

    const data = collectRequestData({ ...requestData, start: currentDate[0], end: currentDate[1] });
    setLoading(true);
    if (workflow) {
      await dispatch(getDashboardUserWorkflowRequest(data));
    } else {
      await dispatch(getDashboardRepScorecard(data));
    }
    setLoading(false);
  };

  const clearFilters = async () => {
    setLoading(true);

    const defaultRequestData = {
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      page: 1,
      limit: 50,
    };

    setRequestData(defaultRequestData);

    if (workflow) {
      await dispatch(getDashboardUserWorkflowRequest(defaultRequestData));
    } else {
      await dispatch(getDashboardRepScorecard(defaultRequestData));
    }

    setLoading(false);
  };

  const onChangePageData = async (path, count) => {
    const newRequestData = { ...requestData, [path]: count };

    if (path === 'limit') {
      newRequestData.page = 1;
    }

    setRequestData((prev) => ({ ...prev, [path]: count }));
    const data = collectRequestData(newRequestData);
    await dispatch(getDashboardRepScorecard(data));
  };

  const downloadButtonClick = async () => {
    await Api.getDashboardUserWorkflow({ ...requestData, download: true });
  };

  const singleRepPageClick = (name) => {
    if (accountHelpers.getUser().role === 'Admin') {
      const queryRequest = queryToStringify({
        start,
        end,
        name,
        report: 'Rep',
      });

      navigate(`/dashboard/report-single${queryRequest}`);
    }
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.wrapper_header}>
        <div style={{ marginRight: 10 }}>
          <RangeDatePicker
            currentDate={requestData}
            onChangeDate={onChangeDate}
            showLastYear
          />
        </div>

        <div className={classes.button_wrapper}>
          <Button
            title="Clear"
            clearButton
            onClick={clearFilters}
          />
        </div>

        {workflow && (
        <div className={classes.button_wrapper}>
          <Button
            downloadButton
            reverseColor
            className={classes.download_btn}
            title="Download"
            onClick={downloadButtonClick}
          />
        </div>
        )}
      </div>

      <div className={classes.table_wrapper}>
        {loading
          ? <Loader />

          : (
            <TableAndPagination
              limitCounts={limitCounts}
              onClick={singleRepPageClick}
              onChange={onChangePageData}
              data={workflow ? dashboardWorkflowData : dashboardPageData}
              header={workflow ? tableHeaderWorkflow : tableHeader}
              totalPage={+dashboardTotalPage}
              page={+requestData.page - 1}
              limit={+requestData.limit}
              hidePagination={!!workflow}
            />
          )}
      </div>
    </div>
  );
};

RepScorecards.propTypes = {
  workflow: PropTypes.bool,
};

RepScorecards.defaultProps = {
  workflow: false,
};

export default RepScorecards;
